import { Sheet, WorkBook, JSON2SheetOpts} from 'xlsx';
import { CamelCaseToProperCase } from '@/filters';
import { has } from 'lodash';

interface SheetObject {
  sheetName: string;
  sheet: object[];
  columnOrder?: string[];
  skipHeader: boolean;
}

// File Extension
const FILE_EXTENSION = 'xlsx';
let xlsx = null;

export default {
  // Primary export function
  async exportXLS(
    data: object[] | SheetObject[],
    pageName: string = 'Sheet 1',
    documentName: string,
    customHeaders?: {
      [key: string]: string;
    },
    skipHeader: boolean = false ,
  ): Promise<any> {
    const {default: module} = await import('xlsx/dist/xlsx.mini.min.js');
    xlsx = module;
    // Check if we have multiple sheets
    const IS_MULTIPLE_SHEETS = has(data[0], 'sheetName');

    // Create the workbook
    const WORKBOOK: WorkBook = xlsx.utils.book_new();

    if (IS_MULTIPLE_SHEETS) {
      (data as SheetObject[]).forEach((sheetObject: SheetObject) => {
        const WORKSHEET: Sheet = this.generateXLS(
          sheetObject.sheet,
          customHeaders,
          sheetObject.columnOrder,
          sheetObject.skipHeader,
        );
        // Assign worksheet to workbook
        xlsx.utils.book_append_sheet(
          WORKBOOK,
          WORKSHEET,
          this.sanitizeSheetName(sheetObject.sheetName),
        );
      });
    } else {
      // Create the worksheet
      const WORKSHEET: Sheet = this.generateXLS(data, customHeaders, null, skipHeader);

      // Assign worksheet to workbook
      xlsx.utils.book_append_sheet(
        WORKBOOK,
        WORKSHEET,
        this.sanitizeSheetName(pageName),
      );
    }

    // Trigger Download
    this.downloadWorkbook(WORKBOOK, documentName);
  },
  // Takes JSON and outputs a sheet
  generateXLS(
    data: object[],
    customHeaders?: { [key: string]: string },
    headerOrder?: string[],
    skipHeader: boolean = false,
  ): Sheet {
    const OPTIONS: JSON2SheetOpts = {
      skipHeader,
    };

    if (headerOrder) {
      OPTIONS.header = headerOrder.map((header) =>
        CamelCaseToProperCase(header),
      );
    }
    return xlsx.utils.json_to_sheet(
      this.constructXLSHeaders(data, customHeaders),
      OPTIONS,
    );
  },
  // This loops through the array of objects and converts keys to proper case
  // or maps custom headers corresponding key that matches the index
  constructXLSHeaders(
    data: object[],
    customHeaders?: { [key: string]: string },
  ): object[] {
    return data.map((row): object => this.createXLSObject(row, customHeaders));
  },
  // Helper to break out the construction of the object
  // This is the primary logic for renaming or mapping headers
  createXLSObject(
    row: object,
    customHeaders?: { [key: string]: string },
  ): object {
    return Object.keys(row).reduce(
      (accumulator: object, key: string): object => {
        const NEW_KEY: string =
          customHeaders && customHeaders[key]
            ? customHeaders[key]
            : CamelCaseToProperCase(key);
        return {
          ...accumulator,
          ...{
            [NEW_KEY]: row[key],
          },
        };
      },
      {},
    );
  },
  // Simple download function
  downloadWorkbook(workbook: WorkBook, documentName: string): void {
    xlsx.writeFile(workbook, `${documentName}.${FILE_EXTENSION}`);
  },
  sanitizeSheetName(sheetName: string) {
    // https://social.msdn.microsoft.com/Forums/en-US/84c0c4d2-52b9-4502-bece-fdc616db05f8/invalid-characters-for-excel-sheet-names?forum=vsto
    // Sheet Name cannot have :, \, /, ?, *, [, ], _ and cannot be longer than 31 characters
    sheetName = sheetName.replace(/[\:\,\\\/\*\?\[\]\_]/g, '-');
    return sheetName.length <= 31 ? sheetName : `${sheetName.substr(0, 28)}...`;
  },
};
